import { Head, router } from "@inertiajs/react";
import {
  AlertCircle,
  Building2,
  FileText,
  Lock,
  Unlock,
  Upload,
} from "lucide-react";
import { useState } from "react";
import BizminerAdminLayout from "@/bizminer/layouts/admin/BizminerAdminLayout.tsx";
import {
  bizminer_admin_organization_account_path,
  upload_bizminer_admin_organization_account_pillar_insight_path,
} from "@/routes";
import type {
  BizminerAdminAccount,
  BizminerAdminOrganizationList,
  BizminerPillarInsight,
} from "@/types/serializers";
import PageHeader from "../Organizations/_components/PageHeader";

interface Props {
  account: BizminerAdminAccount;
  organization: BizminerAdminOrganizationList;
  pillar_insight: BizminerPillarInsight;
  error?: string;
}

export default function New({
  account,
  organization,
  pillar_insight,
  error,
}: Props) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files?.[0]) {
      setSelectedFile(files[0]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedFile) {
      alert("Please select a file to upload");
      return;
    }

    setUploading(true);

    const formData = new FormData();
    formData.append("file", selectedFile);

    router.post(
      upload_bizminer_admin_organization_account_pillar_insight_path(
        organization.id,
        account.id,
        pillar_insight.id,
      ),
      formData,
      {
        onFinish: () => setUploading(false),
      },
    );
  };

  return (
    <BizminerAdminLayout>
      <Head
        title={`Upload ${pillar_insight.title} - ${account.name} - Super Admin`}
      />

      <div className="min-h-screen">
        <PageHeader
          title={`Upload Pillar Insight: ${pillar_insight.title}`}
          backUrl={bizminer_admin_organization_account_path(
            organization.id,
            account.id,
          )}
          backText={`← Back to ${account.name}`}
          icon={
            pillar_insight.unlocked ? (
              <Unlock className="h-8 w-8 text-chart-6" />
            ) : (
              <Lock className="h-8 w-8 text-muted-foreground" />
            )
          }
        />

        <div className="max-w-3xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            {/* Context Info */}
            <div className="bg-card shadow rounded-lg p-6 mb-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Building2 className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-card-foreground">
                      {account.name}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {organization.name}
                    </div>
                  </div>
                </div>
                <div className="border-t border-border pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-card-foreground">
                        {pillar_insight.title}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono">
                        {pillar_insight.slug}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {pillar_insight.unlocked ? (
                        <>
                          <Unlock className="h-4 w-4 text-chart-6" />
                          <span className="text-xs text-chart-6">Unlocked</span>
                        </>
                      ) : (
                        <>
                          <Lock className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">
                            Locked
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-destructive" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-destructive">
                      Upload Failed
                    </h3>
                    <div className="mt-2 text-sm text-destructive/80 whitespace-pre-wrap">
                      {error}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* File Upload */}
              <div className="bg-card shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-card-foreground mb-4">
                  Select YAML File
                </h3>

                <div className="mt-2">
                  <label
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    className={`flex flex-col items-center px-4 py-6 bg-background border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                      isDragging
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary"
                    }`}
                  >
                    <Upload
                      className={`h-12 w-12 ${isDragging ? "text-primary" : "text-muted-foreground"}`}
                    />
                    <span className="mt-2 text-sm text-muted-foreground">
                      {selectedFile
                        ? selectedFile.name
                        : isDragging
                          ? "Drop file here"
                          : "Click to select or drag & drop a YAML file"}
                    </span>
                    <input
                      type="file"
                      accept=".yml,.yaml"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </label>
                </div>

                {selectedFile && (
                  <div className="mt-4 flex items-center space-x-2 text-sm text-muted-foreground">
                    <FileText className="h-4 w-4" />
                    <span>
                      {selectedFile.name} (
                      {(selectedFile.size / 1024).toFixed(2)} KB)
                    </span>
                  </div>
                )}

                <p className="mt-4 text-sm text-muted-foreground">
                  Upload a YAML file containing pillar insight data. The file
                  will update this specific pillar insight (
                  {pillar_insight.title}) for {account.name}.
                </p>
              </div>

              {/* Submit */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() =>
                    router.visit(
                      bizminer_admin_organization_account_path(
                        organization.id,
                        account.id,
                      ),
                    )
                  }
                  className="bg-background py-2 px-4 border border-input rounded-md shadow-sm text-sm font-medium text-card-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading || !selectedFile}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? "Uploading..." : "Upload & Import"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </BizminerAdminLayout>
  );
}
